/* ======== 1. PRODUCT GALLERY ======== */
if (!customElements.get('product-gallery')) {
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.thumbnails = this.querySelectorAll('[data-thumbnail-index]');
      this.mediaItems = this.querySelectorAll('[data-media-index]');
      this.activeIndex = parseInt(this.dataset.initialIndex || '0', 10);
      this.mediaCount = this.mediaItems.length;

      /* Zara-style per-color gallery: each media item carries data-media-color (from an alt token) */
      this.dedicatedColors = [];
      var self = this;
      this.mediaItems.forEach(function(item) {
        var c = item.dataset.mediaColor || '';
        if (c && self.dedicatedColors.indexOf(c) === -1) self.dedicatedColors.push(c);
      });

      this._handleClick = this._handleClick.bind(this);
      this.addEventListener('click', this._handleClick);

      this._initZoom();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._handleClick);
      this._destroyZoom();
    }

    _handleClick(e) {
      var btn = e.target.closest('[data-thumbnail-index]');
      if (btn) {
        this.switchMedia(parseInt(btn.dataset.thumbnailIndex, 10));
        return;
      }

      if (e.target.closest('[data-gallery-prev]')) {
        this.switchMedia(this.activeIndex <= 0 ? this.mediaCount - 1 : this.activeIndex - 1);
        return;
      }

      if (e.target.closest('[data-gallery-next]')) {
        this.switchMedia(this.activeIndex >= this.mediaCount - 1 ? 0 : this.activeIndex + 1);
        return;
      }
    }

    switchMedia(index) {
      if (index === this.activeIndex) return;

      this.mediaItems.forEach(function(item) {
        item.classList.add('opacity-0', 'pointer-events-none', 'absolute', 'inset-0');
        item.classList.remove('opacity-100', 'relative');
      });
      var target = this.querySelector('[data-media-index="' + index + '"]');
      if (target) {
        target.classList.remove('opacity-0', 'pointer-events-none', 'absolute', 'inset-0');
        target.classList.add('opacity-100', 'relative');
      }

      this.thumbnails.forEach(function(thumb) {
        thumb.classList.remove('border-gray-300');
        thumb.classList.add('border-transparent');
      });
      var activeThumb = this.querySelector('[data-thumbnail-index="' + index + '"]');
      if (activeThumb) {
        activeThumb.classList.remove('border-transparent');
        activeThumb.classList.add('border-gray-300');
      }

      this.activeIndex = index;
      this._destroyZoom();
      this._initZoom();
    }

    /* ---- Zara-style: show only the selected color's image set ---- */
    filterByColor(color) {
      if (!this.dedicatedColors.length) return; /* product has no per-color media → keep showing all */
      var colorHasMedia = this.dedicatedColors.indexOf(color) !== -1;
      var self = this;
      var firstVisible = null;

      this.mediaItems.forEach(function(item) {
        var mc = item.dataset.mediaColor || '';
        /* When the chosen color has its own set: show only that set.
           Otherwise (color has no dedicated images): show the shared/untagged images. */
        var visible = colorHasMedia ? (mc === color) : (mc === '');
        var idx = item.dataset.mediaIndex;
        var thumb = self.querySelector('[data-thumbnail-index="' + idx + '"]');
        if (visible) {
          item.classList.remove('hidden');
          if (thumb) thumb.classList.remove('hidden');
          if (firstVisible === null) firstVisible = parseInt(idx, 10);
        } else {
          item.classList.add('hidden');
          if (thumb) thumb.classList.add('hidden');
        }
      });

      /* Safety: never leave the gallery empty */
      if (firstVisible === null) {
        this.mediaItems.forEach(function(item) {
          item.classList.remove('hidden');
          var thumb = self.querySelector('[data-thumbnail-index="' + item.dataset.mediaIndex + '"]');
          if (thumb) thumb.classList.remove('hidden');
        });
        firstVisible = 0;
      }

      this.activeIndex = -1; /* force switchMedia to re-render even if index matches */
      this.switchMedia(firstVisible);
    }

    /* ---- Inline Zoom ---- */
    _initZoom() {
      var container = this.querySelector('[data-media-index="' + this.activeIndex + '"] [data-zoom-container]');
      if (!container) return;

      this._zoomContainer = container;
      this._zoomImg = container.querySelector('[data-zoom-image]');
      if (!this._zoomImg) return;

      this._onMouseMove = this._onMouseMove.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);

      container.addEventListener('mousemove', this._onMouseMove);
      container.addEventListener('mouseleave', this._onMouseLeave);
    }

    _destroyZoom() {
      if (this._zoomContainer) {
        this._zoomContainer.removeEventListener('mousemove', this._onMouseMove);
        this._zoomContainer.removeEventListener('mouseleave', this._onMouseLeave);
      }
      this._zoomContainer = null;
      this._zoomImg = null;
    }

    _onMouseMove(e) {
      var rect = this._zoomContainer.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;

      this._zoomImg.style.transformOrigin = x + '% ' + y + '%';
      this._zoomImg.style.transform = 'scale(2)';

      var hiResSrc = this._zoomImg.dataset.zoomSrc;
      if (hiResSrc && this._zoomImg.src !== hiResSrc) {
        this._zoomImg.src = hiResSrc;
      }
    }

    _onMouseLeave() {
      this._zoomImg.style.transform = 'scale(1)';
    }
  }
  customElements.define('product-gallery', ProductGallery);
}

/* ======== 2. VARIANT SELECTOR ======== */
if (!customElements.get('variant-selector')) {
  class VariantSelector extends HTMLElement {
    connectedCallback() {
      var jsonEl = this.querySelector('[data-product-variants]');
      this.variants = jsonEl ? JSON.parse(jsonEl.textContent) : [];
      this.options = [];

      this._isProductPage = !!this.closest('[data-main-product]');
      this._abortController = null;

      this._handleOptionClick = this._handleOptionClick.bind(this);
      this.addEventListener('click', this._handleOptionClick);

      this._readCurrentOptions();
      this._prefetchVariantImages();
      this._initColorFilter();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._handleOptionClick);
    }

    _readCurrentOptions() {
      var optionGroups = this.querySelectorAll('[data-option-index]');
      var self = this;
      self.options = [];
      optionGroups.forEach(function(group) {
        var pressed = group.querySelector('[aria-pressed="true"]');
        self.options.push(pressed ? pressed.dataset.optionValue : '');
      });
    }

    _handleOptionClick(e) {
      var btn = e.target.closest('[data-option-value]');
      if (!btn) return;

      var group = btn.closest('[data-option-index]');
      if (!group) return;

      var index = parseInt(group.dataset.optionIndex, 10);
      var value = btn.dataset.optionValue;
      this.options[index] = value;

      this._updateOptionUI(group, btn, index);

      if (group.dataset.optionType === 'color') {
        this._updateGallery(btn);
      }

      var variant = this._findVariant();
      if (variant) {
        this._updateVariantUI(variant);
        if (this._isProductPage) {
          this._updateURL(variant.id);
          this._fetchSection(variant.id);
        } else {
          this._updatePriceLocal(variant);
          this._updateStockLocal(variant);
        }
      } else {
        if (this._isProductPage) {
          this._updateSoldOut();
        } else {
          this._updateStockLocal({ available: false });
        }
      }
    }

    _updateOptionUI(group, activeBtn, optionIndex) {
      var isColorGroup = group.dataset.optionType === 'color';

      /* Update label text */
      var label = group.parentElement.querySelector('[data-option-label]');
      if (label) {
        label.textContent = activeBtn.dataset.optionValue;
      }

      /* Update button states */
      var buttons = group.querySelectorAll('[data-option-value]');
      if (isColorGroup) {
        buttons.forEach(function(b) {
          b.classList.remove('border-current', 'ring-1', 'ring-inset', 'ring-white');
          b.classList.add('border-gray-200');
          b.setAttribute('aria-pressed', 'false');
        });
        activeBtn.classList.remove('border-gray-200');
        activeBtn.classList.add('border-current', 'ring-1', 'ring-inset', 'ring-white');
        activeBtn.setAttribute('aria-pressed', 'true');
      } else {
        buttons.forEach(function(b) {
          b.classList.remove('border-black', 'border-2', 'font-semibold');
          if (!b.classList.contains('border')) b.classList.add('border');
          b.classList.add('border-gray-300');
          b.setAttribute('aria-pressed', 'false');
        });
        activeBtn.classList.remove('border-gray-300', 'border');
        activeBtn.classList.add('border-black', 'border-2', 'font-semibold');
        activeBtn.setAttribute('aria-pressed', 'true');
      }
    }

    _findVariant() {
      var opts = this.options;
      return this.variants.find(function(v) {
        var match = true;
        if (opts[0] && v.option1 !== opts[0]) match = false;
        if (opts[1] && v.option2 !== opts[1]) match = false;
        if (opts[2] && v.option3 !== opts[2]) match = false;
        return match;
      }) || null;
    }

    _updateVariantUI(variant) {
      var variantInput = this.querySelector('[data-variant-id]');
      if (variantInput) variantInput.value = variant.id;
    }

    /* ---- Client-side updates (for quick view modal) ---- */
    _formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    _updatePriceLocal(variant) {
      var priceEl = this.querySelector('[id^="price-"]');
      if (!priceEl) return;

      var html = '<div><span class="text-sm font-light">' + this._formatMoney(variant.price) + '</span>';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        var classicRetail = this.dataset.i18nClassicRetail || 'Comparable retail elsewhere';
        html += '</div><div>';
        html += '<span class="text-sm font-light line-through text-gray-400">' + this._formatMoney(variant.compare_at_price) + '</span>';
        html += '<div class="text-sm font-light text-gray-400 mt-0.5">' + classicRetail + '</div>';
      }
      html += '</div>';
      priceEl.innerHTML = html;
    }

    _updateStockLocal(variant) {
      var stockEl = this.querySelector('[id^="stock-"]');
      if (!stockEl) return;

      var isBestSeller = this.dataset.isBestSeller === 'true';
      var bufferPct = parseFloat(this.dataset.bufferPercent || '0') || 0;
      var estimatedDeliveryFrom = this.dataset.estimatedDeliveryFrom || '';
      var estimatedDeliveryTo = this.dataset.estimatedDeliveryTo || '';
      var t = {
        addToCart: this.dataset.i18nAddToCart || 'ADD TO CART',
        addPreorder: this.dataset.i18nAddPreorder || 'ADD PRE-ORDER TO CART',
        readyText: this.dataset.i18nReadyText || 'In-Stock: Ready to Ship',
        notAvailable: this.dataset.i18nNotAvailable || 'Not available to ship right now',
        preorderEstimate: this.dataset.i18nPreorderEstimate || 'Wait & Save: Estimated to Ship __DATE__',
        guarantee: this.dataset.i18nGuarantee || '',
        outOfStock: this.dataset.i18nOutOfStock || 'Out of Stock',
        emailPlaceholder: this.dataset.i18nEmailPlaceholder || 'Your Email Address',
        emailWaitlist: this.dataset.i18nEmailWaitlist || 'Email for waitlist',
        joinWaitlist: this.dataset.i18nJoinWaitlist || 'JOIN WAITLIST'
      };

      if (variant.available) {
        var self = this;
        var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        var btnBase = 'w-full py-3 md:py-4 text-white text-center';
        var bg = 'background-color: var(--color-accent-bg, #3a3153)';
        /* Two-line button: line 1 = label - price (+ struck compare), line 2 = note. Mirrors product-form.liquid */
        var btnHtml = function(label, price, compare, note) {
          var l1 = '<span class="block text-sm font-semibold tracking-wider uppercase">' + label + ' - ' + self._formatMoney(price);
          if (hasCompare) l1 += ' <span class="line-through" style="opacity:.6">' + self._formatMoney(compare) + '</span>';
          l1 += '</span>';
          return l1 + '<span class="block text-xs font-normal mt-0.5">' + note + '</span>';
        };
        var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        var html = '';

        if (isBestSeller) {
          html += '<form method="post" action="' + cartAddUrl + '">'
            + '<input type="hidden" name="id" value="' + variant.id + '" data-variant-id>'
            + '<button type="submit" class="' + btnBase + '" style="' + bg + '">'
            + btnHtml(t.addToCart, variant.price, variant.compare_at_price, t.readyText)
            + '</button></form>';
        } else {
          var mult = 100 + bufferPct;
          var bufPrice = Math.floor(variant.price * mult / 100);
          var bufCompare = Math.floor((variant.compare_at_price || 0) * mult / 100);
          html += '<button type="button" disabled aria-disabled="true" class="' + btnBase + '" style="' + bg + '; opacity:.45; cursor:not-allowed">'
            + btnHtml(t.addToCart, bufPrice, variant.compare_at_price, t.notAvailable)
            + '</button>';
          var estimateNote = t.preorderEstimate.replace('__DATE__', estimatedDeliveryTo);
          html += '<form method="post" action="' + cartAddUrl + '">'
            + '<input type="hidden" name="id" value="' + variant.id + '" data-variant-id>'
            + '<input type="hidden" name="properties[_pre_order]" value="true">'
            + '<input type="hidden" name="properties[_estimated_delivery_from]" value="' + estimatedDeliveryFrom + '">'
            + '<input type="hidden" name="properties[_estimated_delivery_to]" value="' + estimatedDeliveryTo + '">'
            + '<input type="hidden" name="properties[Expected Ship Date]" value="' + estimatedDeliveryFrom + ' - ' + estimatedDeliveryTo + '">'
            + '<button type="submit" class="' + btnBase + '" style="background-color: #311745">'
            + btnHtml(t.addPreorder, variant.price, variant.compare_at_price, estimateNote)
            + '</button></form>';
          if (t.guarantee) html += '<p class="text-xs text-center text-gray-500 mt-1">' + t.guarantee + '</p>';
        }
        stockEl.innerHTML = html;
      } else {
        var productHandle = this.dataset.productHandle || '';
        var productTitle = this.dataset.productTitle || '';
        var variantTitle = variant.title || '';
        var variantHandle = variantTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        var formId = 'Waitlist-' + productHandle + '-' + variant.id;
        stockEl.innerHTML =
          '<p class="text-sm text-center" style="color: #7a748f">' + t.outOfStock + '</p>' +
          '<form method="post" action="/contact#' + formId + '" id="' + formId + '" accept-charset="UTF-8" class="contact-form">' +
            '<input type="hidden" name="form_type" value="customer">' +
            '<input type="hidden" name="utf8" value="✓">' +
            '<input type="hidden" name="contact[tags]" value="waitlist,' + productHandle + ',' + variantHandle + '">' +
            '<input type="hidden" name="contact[note]" value="' + this._escapeAttr(productTitle + ' - ' + variantTitle) + '">' +
            '<input type="email" name="contact[email]" placeholder="' + this._escapeAttr(t.emailPlaceholder) + '" required aria-label="' + this._escapeAttr(t.emailWaitlist) + '" class="w-full px-4 py-4 border border-gray-300">' +
            '<button type="submit" class="w-full py-3 md:py-4 text-sm font-semibold tracking-wider uppercase mt-3 md:mt-4 text-white" style="background-color: var(--color-accent-bg, #3a3153)">' + t.joinWaitlist + '</button>' +
          '</form>';
      }
    }

    _escapeAttr(str) {
      return String(str).replace(/[&<>"']/g, function(m) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m];
      });
    }

    /* ---- Server-side updates (for product page) ---- */
    _updateSoldOut() {
      var firstAvailable = this.variants.find(function(v) { return v.available; });
      if (firstAvailable) {
        this._fetchSection(firstAvailable.id);
      }
    }

    _updateURL(variantId) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      history.replaceState(null, '', url.toString());
    }

    _galleryEl() {
      return this.closest('[data-main-product]')
        ? document.querySelector('product-gallery')
        : this.closest('[data-quick-view-modal]')
          ? this.closest('[data-quick-view-modal]').querySelector('product-gallery')
          : null;
    }

    _updateGallery(btn) {
      var gallery = this._galleryEl();
      if (!gallery) return;
      /* Zara-style: when this product has per-color image sets, swap the whole set */
      if (gallery.dedicatedColors && gallery.dedicatedColors.length) {
        gallery.filterByColor(btn.dataset.optionValue);
        return;
      }
      /* Default: just jump to the color's featured image */
      var index = btn.dataset.mediaIndex;
      if (index !== undefined) gallery.switchMedia(parseInt(index, 10));
    }

    _initColorFilter() {
      var self = this;
      setTimeout(function() {
        var gallery = self._galleryEl();
        if (!gallery || !gallery.dedicatedColors || !gallery.dedicatedColors.length) return;
        var colorGroup = self.querySelector('[data-option-type="color"]');
        if (!colorGroup) return;
        var pressed = colorGroup.querySelector('[aria-pressed="true"]');
        if (pressed) gallery.filterByColor(pressed.dataset.optionValue);
      }, 0);
    }

    _prefetchVariantImages() {
      var gallery = this.closest('[data-main-product]')
        ? document.querySelector('product-gallery')
        : this.closest('[data-quick-view-modal]')
          ? this.closest('[data-quick-view-modal]').querySelector('product-gallery')
          : null;
      if (!gallery) return;
      this.querySelectorAll('[data-media-index]').forEach(function(btn) {
        var media = gallery.querySelector('[data-media-index="' + btn.dataset.mediaIndex + '"] img');
        if (media && media.src) new Image().src = media.src;
      });
    }

    _fetchSection(variantId) {
      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();

      var sectionEl = this.closest('.shopify-section');
      if (!sectionEl) return;
      var sectionId = sectionEl.id.replace('shopify-section-', '');
      var url = window.location.pathname + '?variant=' + variantId + '&sections=' + sectionId;

      var oldPrice = document.getElementById('price-' + sectionId);
      var oldStock = document.getElementById('stock-' + sectionId);
      if (oldPrice) oldPrice.style.opacity = '0.5';
      if (oldStock) oldStock.style.opacity = '0.5';

      fetch(url, { signal: this._abortController.signal })
        .then(function(res) { return res.json(); })
        .then(function(data) {
          var html = new DOMParser().parseFromString(data[sectionId], 'text/html');

          var newPrice = html.getElementById('price-' + sectionId);
          if (newPrice && oldPrice) {
            oldPrice.innerHTML = newPrice.innerHTML;
            oldPrice.style.opacity = '';
          }

          var newStock = html.getElementById('stock-' + sectionId);
          if (newStock && oldStock) {
            oldStock.innerHTML = newStock.innerHTML;
            oldStock.style.opacity = '';
          }
        })
        .catch(function(e) {
          if (e.name !== 'AbortError') throw e;
        });
    }
  }
  customElements.define('variant-selector', VariantSelector);
}

/* ======== 3. STICKY ADD TO CART (PDP) ======== */
if (!customElements.get('sticky-atc')) {
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      var sectionId = this.dataset.sectionId;
      this.priceSource = document.getElementById('price-' + sectionId);
      this.stockSource = document.getElementById('stock-' + sectionId);
      this.stickyPrice = this.querySelector('[data-sticky-price]');
      this.thumb = this.querySelector('[data-sticky-thumb]');
      this.addBtn = this.querySelector('[data-sticky-add]');
      this.variantText = this.querySelector('[data-sticky-variant]');
      this.mainSelector = document.querySelector('[data-main-product] variant-selector');
      if (!this.stockSource) return;

      this._available = true;
      this._offscreen = false;
      this._footerVisible = false;

      this._onAddClick = this._onAddClick.bind(this);
      this._syncPrice = this._syncPrice.bind(this);
      this._syncStock = this._syncStock.bind(this);
      this._syncVariant = this._syncVariant.bind(this);

      if (this.addBtn) this.addBtn.addEventListener('click', this._onAddClick);
      /* Reflect the selected color/size as text, updated the moment an option is clicked */
      if (this.mainSelector) this.mainSelector.addEventListener('click', this._syncVariant);

      /* Show the bar only once the original CTA has been scrolled UP out of view
         (above the viewport) — not while it is still below the fold and unreached */
      var self = this;
      this._io = new IntersectionObserver(function(entries) {
        var entry = entries[0];
        self._offscreen = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        self._render();
      }, { threshold: 0 });
      this._io.observe(this.stockSource);

      /* Slide the bar back down once the footer reaches the viewport */
      var footer = document.querySelector('footer');
      if (footer) {
        this._footerIo = new IntersectionObserver(function(entries) {
          self._footerVisible = entries[0].isIntersecting;
          self._render();
        }, { threshold: 0 });
        this._footerIo.observe(footer);
      }

      /* Mirror live price (re-rendered on variant change) into the bar */
      if (this.priceSource) {
        this._priceObserver = new MutationObserver(this._syncPrice);
        this._priceObserver.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
      }

      /* Toggle the bar's button when availability changes (in stock vs waitlist) */
      this._stockObserver = new MutationObserver(this._syncStock);
      this._stockObserver.observe(this.stockSource, { childList: true, subtree: true });

      this._syncPrice();
      this._syncStock();
      this._syncVariant();
    }

    disconnectedCallback() {
      if (this.addBtn) this.addBtn.removeEventListener('click', this._onAddClick);
      if (this.mainSelector) this.mainSelector.removeEventListener('click', this._syncVariant);
      if (this._io) this._io.disconnect();
      if (this._footerIo) this._footerIo.disconnect();
      if (this._priceObserver) this._priceObserver.disconnect();
      if (this._stockObserver) this._stockObserver.disconnect();
    }

    _syncPrice() {
      if (this.priceSource && this.stickyPrice) {
        var priceSpan = this.priceSource.querySelector('span');
        var compareEl = this.priceSource.querySelector('.line-through');
        var html = '<span class="font-light">' + (priceSpan ? priceSpan.textContent.trim() : '') + '</span>';
        if (compareEl) {
          html += '<span class="font-light line-through text-gray-400">' + compareEl.textContent.trim() + '</span>';
        }
        this.stickyPrice.innerHTML = html;
      }
      this._syncThumb();
    }

    _syncThumb() {
      if (!this.thumb) return;
      var gallery = document.querySelector('[data-main-product] product-gallery');
      if (!gallery) return;
      var activeImg = gallery.querySelector('[data-media-index].opacity-100 img') || gallery.querySelector('[data-media-index] img');
      var src = activeImg && (activeImg.currentSrc || activeImg.src);
      if (src) this.thumb.src = src;
    }

    _syncVariant() {
      if (!this.variantText || !this.mainSelector) return;
      var vals = [];
      this.mainSelector.querySelectorAll('[data-option-index] [aria-pressed="true"]').forEach(function(b) {
        if (b.dataset.optionValue) vals.push(b.dataset.optionValue);
      });
      if (vals.length) this.variantText.textContent = vals.join(' / ');
    }

    _syncStock() {
      this._available = !!this.stockSource.querySelector('form[action*="/cart/add"]');
      this._render();
    }

    _render() {
      /* Visible only when the original CTA is off-screen, the variant is purchasable, and the footer is not yet in view */
      this.classList.toggle('is-visible', this._offscreen && this._available && !this._footerVisible);
    }

    _onAddClick() {
      var form = this.stockSource.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      /* Replay a real click on the native button so any third-party app (e.g. Zevo
         early-access) bound to its click — and the form's pre-order line-item
         properties — behave exactly as if the original button was pressed. */
      var btn = form.querySelector('[type="submit"]');
      if (btn) {
        btn.click();
      } else if (form.requestSubmit) {
        form.requestSubmit();
      }
    }
  }
  customElements.define('sticky-atc', StickyAtc);
}
